
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Language } from '@app/core/enums/Language';
import { environment } from '@env';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import Swal from 'sweetalert2';

export interface CountryRegulation {
  CRG_ID: number;
  CRG_CountryCode: string;
  CRG_IsEuropeanUnion: boolean;
  CRG_IsActive: boolean;
}

export interface GetCountryRegulationsResult {
  CountryRegulations: CountryRegulation[];
  Exceptions?: any[];
}

export interface SaveCountryRegulationResult {
  CRG_ID: number;
  Exceptions?: any[];
}

@Component({
  selector: 'app-countryregulations-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MatIconModule],
  templateUrl: './countryregulations_manager.component.html',
  styleUrls: ['./countryregulations_manager.component.scss']
})

export class CountryregulationsManagerComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<CountryRegulation>();
  @Output() deleted = new EventEmitter<number>();

  regulations: CountryRegulation[] = [];

  isProcessing = false;
  editingId: number | null = null;
  draft: CountryRegulation | null = null;
  adding = false;
  newRow: CountryRegulation = this.blankRow();

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<CountryregulationsManagerComponent>
  ) {}

  ngOnInit(): void {
    this.loadRegulations();
  }

  loadRegulations(): void {
    this.isProcessing = true;

    const Detail = {
      Language: this.getCurrentLanguage()
    };

    this.httpClient.post<GetCountryRegulationsResult>(environment.CSDAEndPoint + '/CSDA/GetCountryRegulations', Detail).subscribe({
      next: (data) => {
        this.isProcessing = false;

        if (this.hasExceptions(data)) return;

        this.regulations = data.CountryRegulations ?? [];
        this.cancel();
      },
      error: (error) => {
        this.isProcessing = false;
        this.showError(error);
      }
    });
  }

   private getCurrentLanguage(): Language {
    const lang = (this.translateService.currentLang || 'en').toLowerCase();
    return lang.startsWith('fr') ? Language.French : Language.English;
  }

  private t(key: string): string {
    return this.translateService.instant('app.countryRegulations.' + key);
  }

  private blankRow(): CountryRegulation {
    return { CRG_ID: 0, CRG_CountryCode: '', CRG_IsEuropeanUnion: false, CRG_IsActive: true };
  }

  private hasExceptions(result: { Exceptions?: any[] }): boolean {
    if (!result?.Exceptions?.length) return false;

    const message = result.Exceptions.map(e => e.Message ?? e.message ?? e.Description ?? String(e)).join('\n');

    Swal.fire({
      icon: 'error',
      text: message,
      confirmButtonText: this.t('ok')
    });

    return true;
  }

  private showError(error: any): void {
    Swal.fire({
      icon: 'error',
      title: error?.error?.message ?? error?.message ?? String(error),
      confirmButtonText: this.t('ok')
    });
  }

  get busy(): boolean {
    return this.editingId !== null || this.adding;
  }

  get displayRows(): CountryRegulation[] {
    return this.regulations.map(r => r.CRG_ID === this.editingId ? this.draft! : r);
  }

  isEditing(row: CountryRegulation): boolean {
    return row.CRG_ID === this.editingId;
  }

  edit(row: CountryRegulation): void {
    if (this.busy || this.isProcessing) return;

    this.editingId = row.CRG_ID;
    this.draft = { ...row };
  }

  beginAdd(): void {
    if (this.busy || this.isProcessing) return;

    this.adding = true;
    this.newRow = this.blankRow();
  }

  cancel(): void {
    this.editingId = null;
    this.draft = null;
    this.adding = false;
    this.newRow = this.blankRow();
  }

  save(row: CountryRegulation, isNew = false): void {
    if (this.isProcessing) return;

    const code = (row.CRG_CountryCode || '').trim().toUpperCase();

    if (!/^[A-Z]{2}$/.test(code)) {
      Swal.fire({ icon: 'warning', text: this.t('invalidCountryCode'), confirmButtonText: this.t('ok') });
      return;
    }

    if (this.regulations.some(r => r.CRG_CountryCode.toUpperCase() === code && (isNew || r.CRG_ID !== row.CRG_ID))) {
      Swal.fire({ icon: 'warning', text: this.t('duplicateCountryCode'), confirmButtonText: this.t('ok') });
      return;
    }

    this.isProcessing = true;

    const value: CountryRegulation = { ...row, CRG_CountryCode: code };

    const Detail = {
      Language: this.getCurrentLanguage(),
      CountryRegulation: value,
      IsDelete: false
    };

    this.httpClient.post<SaveCountryRegulationResult>(environment.CSDAEndPoint + '/CSDA/SaveCountryRegulation', Detail).subscribe({
      next: (data) => {
        this.isProcessing = false;

        if (this.hasExceptions(data)) return;

        value.CRG_ID = data.CRG_ID;

        if (isNew) {
          this.regulations = [...this.regulations, value];
        } else {
          this.regulations = this.regulations.map(r => r.CRG_ID === value.CRG_ID ? value : r);
        }

        // Always sort alphabetically after ADD or EDIT.
        this.regulations.sort((a, b) => a.CRG_CountryCode.localeCompare(b.CRG_CountryCode));

        this.saved.emit(value);
        this.cancel();
      },
      error: (error) => {
        this.isProcessing = false;
        this.showError(error);
      }
    });
  }

  async remove(row: CountryRegulation): Promise<void> {
    if (this.busy || this.isProcessing) return;

    const result = await Swal.fire({
      title: this.t('deleteTitle'),
      text: this.t('deleteMessage').replace('{code}', row.CRG_CountryCode),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: this.t('yes'),
      cancelButtonText: this.t('no'),
      confirmButtonColor: '#dc3545'
    });

    if (!result.isConfirmed) return;

    this.isProcessing = true;

    const Detail = {
      Language: this.getCurrentLanguage(),
      CountryRegulation: row,
      IsDelete: true
    };

    this.httpClient.post<SaveCountryRegulationResult>(environment.CSDAEndPoint + '/CSDA/SaveCountryRegulation', Detail).subscribe({
      next: (data) => {
        this.isProcessing = false;

        if (this.hasExceptions(data)) return;

        this.regulations = this.regulations.filter(r => r.CRG_ID !== row.CRG_ID);
        this.deleted.emit(row.CRG_ID);
      },
      error: (error) => {
        this.isProcessing = false;
        this.showError(error);
      }
    });
  }

  close(): void {
    if (this.isProcessing) return;

    this.closed.emit();
    this.dialogRef.close();
  }
}
